// Permission utility for role-based access control

export type UserRole = "super_admin" | "admin" | "agent";

export interface User {
  _id: string;
  name: string;
  email: string;
  role: UserRole;
  companyId?:
    | string
    | {
        _id: string;
        name: string;
        waAccountId: string;
        subscription: {
          isActive: boolean;
          endDate: string;
        };
      };
  profile?: {
    firstName?: string;
    lastName?: string;
    timezone?: string;
    language?: string;
  };
  preferences?: {
    theme?: string;
    notifications?: {
      email: boolean;
      push: boolean;
      sms: boolean;
      whatsapp: boolean;
    };
    dashboard?: {
      defaultView: string;
      itemsPerPage: number;
    };
  };
  status?: {
    isActive: boolean;
    isOnline: boolean;
    lastLogin?: string;
    lastActivity?: string;
    loginAttempts: number;
    lockUntil?: string;
  };
  agentSettings?: {
    maxConcurrentChats: number;
    autoAssignChats: boolean;
    workingHours: {
      enabled: boolean;
      start?: string;
      end?: string;
      timezone?: string;
      days: string[];
    };
  };
  superAdminSettings?: {
    canCreateCompanies: boolean;
    canManagePackages: boolean;
    canAccessAllCompanies: boolean;
  };
  createdBy?: string;
  updatedBy?: string;
  isDeleted: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

// Permission definitions
export const PERMISSIONS = {
  // Company Management
  COMPANY_CREATE: "company:create",
  COMPANY_READ: "company:read",
  COMPANY_UPDATE: "company:update",
  COMPANY_DELETE: "company:delete",

  // User Management
  USER_CREATE: "user:create",
  USER_READ: "user:read",
  USER_UPDATE: "user:update",
  USER_DELETE: "user:delete",

  // Package Management
  PACKAGE_CREATE: "package:create",
  PACKAGE_READ: "package:read",
  PACKAGE_UPDATE: "package:update",
  PACKAGE_DELETE: "package:delete",

  // WhatsApp Config Management
  WA_CONFIG_CREATE: "wa_config:create",
  WA_CONFIG_READ: "wa_config:read",
  WA_CONFIG_UPDATE: "wa_config:update",
  WA_CONFIG_DELETE: "wa_config:delete",

  // Contact Management
  CONTACT_CREATE: "contact:create",
  CONTACT_READ: "contact:read",
  CONTACT_UPDATE: "contact:update",
  CONTACT_DELETE: "contact:delete",

  // Message Management
  MESSAGE_CREATE: "message:create",
  MESSAGE_READ: "message:read",
  MESSAGE_UPDATE: "message:update",
  MESSAGE_DELETE: "message:delete",
  MESSAGE_SEND: "message:send",

  // Template Management
  TEMPLATE_CREATE: "template:create",
  TEMPLATE_READ: "template:read",
  TEMPLATE_UPDATE: "template:update",
  TEMPLATE_DELETE: "template:delete",

  // Analytics
  ANALYTICS_READ: "analytics:read",

  // Settings
  SETTINGS_READ: "settings:read",
  SETTINGS_UPDATE: "settings:update",
} as const;

// Role-Permission Mapping
const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  super_admin: [
    // Full access to everything
    ...Object.values(PERMISSIONS),
  ],

  admin: [
    // Company resources (limited to their company)
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,

    // WhatsApp Config (limited to their company)
    PERMISSIONS.WA_CONFIG_READ,
    PERMISSIONS.WA_CONFIG_UPDATE,

    // Full access to company resources
    PERMISSIONS.CONTACT_CREATE,
    PERMISSIONS.CONTACT_READ,
    PERMISSIONS.CONTACT_UPDATE,
    PERMISSIONS.CONTACT_DELETE,

    PERMISSIONS.MESSAGE_CREATE,
    PERMISSIONS.MESSAGE_READ,
    PERMISSIONS.MESSAGE_UPDATE,
    PERMISSIONS.MESSAGE_DELETE,
    PERMISSIONS.MESSAGE_SEND,

    PERMISSIONS.TEMPLATE_CREATE,
    PERMISSIONS.TEMPLATE_READ,
    PERMISSIONS.TEMPLATE_UPDATE,
    PERMISSIONS.TEMPLATE_DELETE,

    PERMISSIONS.ANALYTICS_READ,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_UPDATE,
  ],

  agent: [
    // Limited access to company resources
    PERMISSIONS.CONTACT_READ,
    PERMISSIONS.CONTACT_UPDATE,

    PERMISSIONS.MESSAGE_CREATE,
    PERMISSIONS.MESSAGE_READ,
    PERMISSIONS.MESSAGE_SEND,

    PERMISSIONS.TEMPLATE_READ,
  ],
};

// Legacy permission mappings for backward compatibility
const LEGACY_PERMISSIONS: Record<string, string[]> = {
  canManageUsers: [
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
  ],
  canManageTemplates: [
    PERMISSIONS.TEMPLATE_CREATE,
    PERMISSIONS.TEMPLATE_READ,
    PERMISSIONS.TEMPLATE_UPDATE,
    PERMISSIONS.TEMPLATE_DELETE,
  ],
  canSendMessages: [PERMISSIONS.MESSAGE_SEND],
  canAccessAnalytics: [PERMISSIONS.ANALYTICS_READ],
};

// Check if user has specific permission
export const hasPermission = (
  user: User | null,
  permission: string,
): boolean => {
  if (!user) return false;

  const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
  return rolePermissions.includes(permission);
};

// Check if user has any of the specified permissions
export const hasAnyPermission = (
  user: User | null,
  permissions: string[],
): boolean => {
  if (!user) return false;

  const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
  return permissions.some((permission) => rolePermissions.includes(permission));
};

// Check if user has all of the specified permissions
export const hasAllPermissions = (
  user: User | null,
  permissions: string[],
): boolean => {
  if (!user) return false;

  const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
  return permissions.every((permission) =>
    rolePermissions.includes(permission),
  );
};

// Legacy permission check for backward compatibility
export const hasLegacyPermission = (
  user: User | null,
  legacyPermission: string,
): boolean => {
  if (!user) return false;

  const requiredPermissions = LEGACY_PERMISSIONS[legacyPermission] || [];
  return hasAnyPermission(user, requiredPermissions);
};

// Check if user has specific role
export const hasRole = (user: User | null, role: UserRole): boolean => {
  return user?.role === role;
};

// Check if user has any of the specified roles
export const hasAnyRole = (user: User | null, roles: UserRole[]): boolean => {
  return user ? roles.includes(user.role) : false;
};

// Check if user can access company resource
export const canAccessCompanyResource = (
  user: User | null,
  companyId: string,
): boolean => {
  if (!user) return false;

  // Super admin can access all companies
  if (user.role === "super_admin") return true;

  // Admin and Agent can only access their own company
  return user.companyId === companyId;
};

// Get user permissions
export const getUserPermissions = (user: User | null): string[] => {
  if (!user) return [];
  return ROLE_PERMISSIONS[user.role] || [];
};

// Check if user is super admin
export const isSuperAdmin = (user: User | null): boolean => {
  return user?.role === "super_admin";
};

// Check if user is admin
export const isAdmin = (user: User | null): boolean => {
  return user?.role === "admin";
};

// Check if user is agent
export const isAgent = (user: User | null): boolean => {
  return user?.role === "agent";
};

// Check if user is active and not deleted
export const isUserActive = (user: User | null): boolean => {
  return user
    ? user.isActive && !user.isDeleted && (user.status?.isActive ?? true)
    : false;
};
